import {
  DashboardIcon,
  SettingsCogIcon,
} from "@volt_developers/components/components/Icons";

import {
  AdministrationIcon,
  CheckoutIcon,
  CircuitBreakerOutlinedIcon,
  ConnectIcon,
  GatewayOutlinedIcon,
  VoltAccountsIcon,
  VoltStablecoinsIcon,
} from "assets/productIcons";
import { COMMON_TRANSLATION_KEYS } from "constants/commonTranslationsKeys";
import { ACTION_DETAILS, ACTION_LIST } from "models/role/action";
import {
  SUBJECT_ACCOUNT,
  SUBJECT_CASH_MANAGEMENT,
  SUBJECT_CASH_MANAGEMENT_SETTLEMENT,
  SUBJECT_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION,
  SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNT_TRANSACTIONS,
  SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS,
  SUBJECT_CHECKOUT,
  SUBJECT_CIRCUIT_BREAKER,
  SUBJECT_CLIENT,
  SUBJECT_CLOSED_LOOP_PAYOUTS,
  SUBJECT_CREDIT_NOTIFICATION_PREFERENCES,
  SUBJECT_CUSTOMER,
  SUBJECT_CUSTOMER_BANK_LIST,
  SUBJECT_OPEN_LOOP_PAYOUTS,
  SUBJECT_PAYMENT,
  SUBJECT_PAYOUTS_NOTIFICATIONS,
  SUBJECT_REFUND,
  SUBJECT_USER,
  SUBJECT_VX2_ONBOARDING,
  SUBJECT_VX2_PAYMENT,
  SUBJECT_VX2_SETTLEMENT,
  SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
  SUBJECT_VX2_WALLET,
  SUBJECT_WORKFLOWS,
} from "models/role/subject";
import { PAGE_URLS as ACCOUNTS_PAYMENTS_PAGE_URLS } from "modules/accounts-payments/constants/PageUrls";
import { PAGE_URLS as APPLICATIONS_PAGE_URLS } from "modules/applications/constants/PageUrls";
import { PAGE_URLS as ACCOUNTS_PAGE_URLS } from "modules/bank-accounts/constants/PageUrls";
import { PAGE_URLS as CHECKOUT_PAGE_URLS } from "modules/checkout/constants/PageUrls";
import { PAGE_URLS as CIRCUIT_BREAKER_PAGE_URLS } from "modules/circuit-breaker/constants/PageUrls";
import {
  allowlistRoute,
  circuitBreakerRoutes,
  riskRulesRoute,
} from "modules/circuit-breaker/routes";
import { pruneArray } from "modules/common/utils/pruneArray";
import { PAGE_URLS as CONNECT_PAGE_URLS } from "modules/connect/constants/PageUrls";
import { settlementsRoutes } from "modules/connect/routes/settlements";
import { FILTERS_KEYS as CUSTOMER_BANKS_FILTERS_KEYS } from "modules/customer-banks/constants/Filters";
import { useCustomerBankListFeatureFlag } from "modules/customer-banks/hooks/useCustomerBankListFeatureFlag";
import { configurationRoute as customerBanksConfigurationRoute } from "modules/customer-banks/routes";
import { customersRoutes } from "modules/customers/routes";
import { dashboardRoutes } from "modules/dashboard/routes";
import { PAGE_URLS as MANDATES_PAGE_URLS } from "modules/mandates/constants/PageUrls";
import { useMandatesFeatureFlag } from "modules/mandates/hooks/useMandatesFeatureFlag";
import { PAGE_URLS as PAYMENTS_PAGE_URLS } from "modules/payments/constants/PageUrls";
import { payoutsRoutes } from "modules/payouts/routes";
import { refundsRoutes } from "modules/refunds/routes";
import { PAGE_URLS as STABLECOINS_PAGE_URLS } from "modules/stablecoins/constants/PageUrls";
import { userManagementRoutes } from "modules/user-management/routes";
import { PAGE_URLS as VOLT_ACCOUNTS_PAGE_URLS } from "modules/volt-accounts/constants/PageUrls";
import { workflowsRoutes } from "modules/workflows/routes";
import { administrationRoutes } from "routes/administration";
import { configurationRoutes } from "routes/configuration";

import { GATEWAY_PATH } from "../../constants/Payments";
import { SIDEBAR_TEST_IDS } from "../../constants/TestIds";
import { SIDEBAR_IDS } from "./SidebarIds";

import type { ReactNode } from "react";

import type { PermissionObject } from "../../types/permissions";

export interface SidebarConfigItem {
  id: string;
  permissions: PermissionObject[];
  title: string;
  children?: SidebarConfigItem[];
  "data-testid"?: string;
  icon?: ReactNode;
  isActive?: (path: string, activePath: string) => boolean;
  permissionOneOf?: boolean;
  url?: string;
}

export type DirtySidebarConfigItem = SidebarConfigItem | null;

export function useSidebarConfig() {
  const hasMandates = useMandatesFeatureFlag();
  const hasCustomerBankList = useCustomerBankListFeatureFlag();

  const dirtySidebarConfig: DirtySidebarConfigItem[] = [
    {
      id: SIDEBAR_IDS.dashboard,
      title: COMMON_TRANSLATION_KEYS.Dashboard,
      url: dashboardRoutes.path,
      isActive,
      icon: <DashboardIcon shouldScale />,
      "data-testid": SIDEBAR_TEST_IDS.DASHBOARD,
      permissions: [],
    },
    {
      id: SIDEBAR_IDS.transactions,
      title: COMMON_TRANSLATION_KEYS.paymentGateway,
      url: GATEWAY_PATH,
      isActive,
      icon: <GatewayOutlinedIcon />,
      "data-testid": SIDEBAR_TEST_IDS.TRANSACTIONS,
      permissionOneOf: true,
      permissions: [
        {
          do: ACTION_LIST,
          on: SUBJECT_PAYMENT,
        },
      ],
      children: pruneArray([
        {
          id: SIDEBAR_IDS.transactionsPayments,
          title: COMMON_TRANSLATION_KEYS.payments,
          url: PAYMENTS_PAGE_URLS.getList(),
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.PAYMENTS,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_PAYMENT,
            },
          ],
        },
        hasMandates
          ? {
              id: SIDEBAR_IDS.transactionsMandates,
              title: COMMON_TRANSLATION_KEYS.mandates,
              url: MANDATES_PAGE_URLS.list,
              isActive,
              "data-testid": SIDEBAR_TEST_IDS.MANDATES,
              permissions: [
                {
                  do: ACTION_LIST,
                  on: SUBJECT_PAYMENT,
                },
              ],
            }
          : null,
      ]),
    },
    {
      id: SIDEBAR_IDS.connectGroup,
      title: COMMON_TRANSLATION_KEYS.connect,
      url: VOLT_ACCOUNTS_PAGE_URLS.list,
      isActive: (path: string, activePath: string) =>
        activePath.startsWith(CONNECT_PAGE_URLS.core) ||
        isActive(path, activePath),
      icon: <ConnectIcon />,
      "data-testid": SIDEBAR_TEST_IDS.CONNECT,
      permissionOneOf: true,
      permissions: [
        {
          do: ACTION_LIST,
          on: SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNT_TRANSACTIONS,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_CASH_MANAGEMENT,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_REFUND,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_CLOSED_LOOP_PAYOUTS,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_OPEN_LOOP_PAYOUTS,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_CASH_MANAGEMENT_SETTLEMENT,
        },
      ],
      children: [
        {
          id: SIDEBAR_IDS.voltAccountsOverview,
          title: COMMON_TRANSLATION_KEYS.overview,
          url: VOLT_ACCOUNTS_PAGE_URLS.list,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.VOLT_ACCOUNTS_OVERVIEW,
          permissionOneOf: true,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS,
            },
            {
              do: ACTION_LIST,
              on: SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNT_TRANSACTIONS,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.voltAccountsConnect,
          title: COMMON_TRANSLATION_KEYS.connect,
          url: CONNECT_PAGE_URLS.connect,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.VOLT_ACCOUNTS_CONNECT,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_CASH_MANAGEMENT,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.transactionsRefunds,
          title: COMMON_TRANSLATION_KEYS.refunds,
          url: refundsRoutes.path,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.REFUNDS,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_REFUND,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.transactionsPayouts,
          title: COMMON_TRANSLATION_KEYS.payouts,
          url: payoutsRoutes.path,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.PAYOUTS,
          permissionOneOf: true,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_CLOSED_LOOP_PAYOUTS,
            },
            {
              do: ACTION_LIST,
              on: SUBJECT_OPEN_LOOP_PAYOUTS,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.transactionsSettlements,
          title: COMMON_TRANSLATION_KEYS.settlements,
          url: settlementsRoutes[0].path,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.SETTLEMENTS,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_CASH_MANAGEMENT_SETTLEMENT,
            },
          ],
        },
      ],
    },
    {
      id: SIDEBAR_IDS.voltAccounts,
      title: COMMON_TRANSLATION_KEYS.accountsSidebarTitle,
      url: VOLT_ACCOUNTS_PAGE_URLS.accounts,
      isActive,
      icon: <VoltAccountsIcon />,
      "data-testid": SIDEBAR_TEST_IDS.ACCOUNTS,
      permissionOneOf: true,
      permissions: [
        {
          do: ACTION_LIST,
          on: SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS,
        },
      ],
      children: getVoltAccountsChildren(),
    },
    {
      id: SIDEBAR_IDS.checkoutBranding,
      title: COMMON_TRANSLATION_KEYS.checkoutBranding,
      url: CHECKOUT_PAGE_URLS.legacyDetails,
      isActive,
      icon: <CheckoutIcon />,
      "data-testid": SIDEBAR_TEST_IDS.CHECKOUT_BRANDING,
      permissions: [
        {
          do: ACTION_DETAILS,
          on: SUBJECT_CHECKOUT,
        },
      ],
    },
    {
      id: SIDEBAR_IDS.circuitBreaker,
      title: COMMON_TRANSLATION_KEYS.circuitBreaker,
      icon: <CircuitBreakerOutlinedIcon />,
      url: circuitBreakerRoutes?.path,
      isActive,
      "data-testid": SIDEBAR_TEST_IDS.CIRCUIT_BREAKER,
      permissions: [
        {
          do: ACTION_LIST,
          on: SUBJECT_CIRCUIT_BREAKER,
        },
      ],
      children: [
        {
          id: SIDEBAR_IDS.circuitBreakerAllowList,
          title: COMMON_TRANSLATION_KEYS.allowlist,
          url: allowlistRoute.path,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.ALLOWLIST,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_CIRCUIT_BREAKER,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.circuitBreakerBlockList,
          title: COMMON_TRANSLATION_KEYS.blocklist,
          url: CIRCUIT_BREAKER_PAGE_URLS.getBlocklist(),
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.BLOCKLIST,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_CIRCUIT_BREAKER,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.circuitBreakerRiskRules,
          title: COMMON_TRANSLATION_KEYS.riskRules,
          url: riskRulesRoute.path,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.RISK_RULES,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_CIRCUIT_BREAKER,
            },
          ],
        },
      ],
    },
    {
      id: SIDEBAR_IDS.stablecoins,
      title: COMMON_TRANSLATION_KEYS.stablecoins,
      icon: <VoltStablecoinsIcon />,
      url: STABLECOINS_PAGE_URLS.onboarding,
      isActive,
      "data-testid": SIDEBAR_TEST_IDS.STABLECOINS,
      permissionOneOf: true,
      permissions: [
        {
          do: ACTION_DETAILS,
          on: SUBJECT_VX2_ONBOARDING,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_VX2_PAYMENT,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_VX2_WALLET,
        },
        {
          do: ACTION_DETAILS,
          on: SUBJECT_VX2_SETTLEMENT,
        },
        {
          do: ACTION_DETAILS,
          on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
        },
      ],
      children: [
        {
          id: SIDEBAR_IDS.stablecoinsOnboarding,
          title: COMMON_TRANSLATION_KEYS.onboarding,
          url: STABLECOINS_PAGE_URLS.onboarding,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.STABLECOINS_ONBOARDING,
          permissionOneOf: true,
          permissions: [
            {
              do: ACTION_DETAILS,
              on: SUBJECT_VX2_ONBOARDING,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.stablecoinsWallets,
          title: COMMON_TRANSLATION_KEYS.wallets,
          url: STABLECOINS_PAGE_URLS.wallets,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.STABLECOINS_WALLETS,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_VX2_WALLET,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.stablecoinsBeneficiaries,
          title: COMMON_TRANSLATION_KEYS.beneficiaries,
          url: STABLECOINS_PAGE_URLS.beneficiaries,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.STABLECOINS_BENEFICIARIES,
          permissions: [
            {
              do: ACTION_DETAILS,
              on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.stablecoinsSettlementDestinations,
          title: COMMON_TRANSLATION_KEYS.settlementDestinations,
          url: STABLECOINS_PAGE_URLS.settlementDestinations,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.STABLECOINS_SETTLEMENT_DESTINATIONS,
          permissions: [
            {
              do: ACTION_DETAILS,
              on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.stablecoinsPayments,
          title: COMMON_TRANSLATION_KEYS.payments,
          url: STABLECOINS_PAGE_URLS.payments,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.STABLECOINS_PAYMENTS,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_VX2_PAYMENT,
            },
          ],
        },
      ],
    },
    workflowsSidebarConfig,
    {
      id: SIDEBAR_IDS.configuration,
      title: COMMON_TRANSLATION_KEYS.configuration,
      icon: <SettingsCogIcon shouldScale />,
      url: configurationRoutes.path,
      isActive,
      "data-testid": SIDEBAR_TEST_IDS.CONFIGURATION,
      permissions: [
        {
          do: ACTION_LIST,
          on: SUBJECT_CLIENT,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_ACCOUNT,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_CUSTOMER,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_USER,
        },
      ],
      permissionOneOf: true,
      children: pruneArray([
        {
          id: SIDEBAR_IDS.configurationApplications,
          title: COMMON_TRANSLATION_KEYS.Applications,
          url: APPLICATIONS_PAGE_URLS.getList(),
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.APPLICATIONS,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_CLIENT,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.configurationBankAccount,
          title: COMMON_TRANSLATION_KEYS["Bank accounts"],
          url: ACCOUNTS_PAGE_URLS.getList(),
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.ACCOUNTS,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_ACCOUNT,
            },
          ],
        },
        hasCustomerBankList
          ? {
              id: SIDEBAR_IDS.customerBanksConfiguration,
              title: COMMON_TRANSLATION_KEYS.bankList,
              url: `${customerBanksConfigurationRoute.path}?${CUSTOMER_BANKS_FILTERS_KEYS.PIS_STATUS}=true`,
              isActive: (_, activePath) =>
                activePath.startsWith(customerBanksConfigurationRoute.path),
              "data-testid": SIDEBAR_TEST_IDS.CUSTOMER_BANKS_CONFIGURATION,
              permissions: [
                {
                  do: ACTION_LIST,
                  on: SUBJECT_CUSTOMER_BANK_LIST,
                },
              ],
            }
          : null,
        {
          id: SIDEBAR_IDS.configurationCustomers,
          title: COMMON_TRANSLATION_KEYS.customers,
          url: customersRoutes.path,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.CUSTOMERS,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_CUSTOMER,
            },
          ],
        },
        {
          id: SIDEBAR_IDS.configurationUsers,
          title: COMMON_TRANSLATION_KEYS.Users,
          url: userManagementRoutes.path,
          isActive,
          "data-testid": SIDEBAR_TEST_IDS.USERS,
          permissions: [
            {
              do: ACTION_LIST,
              on: SUBJECT_USER,
            },
          ],
        },
      ]),
    },
  ];

  const sidebarConfig = dirtySidebarConfig.filter(
    (item) => item
  ) as SidebarConfigItem[];

  return {
    sidebarConfig,
  };
}

function isActive(path: string, activePath: string) {
  if (path === "/") {
    return path === activePath;
  }

  return activePath.startsWith(path);
}

const workflowsSidebarConfig: SidebarConfigItem = {
  id: SIDEBAR_IDS.administration,
  title: COMMON_TRANSLATION_KEYS.Administration,
  icon: <AdministrationIcon />,
  url: administrationRoutes.path,
  isActive,
  "data-testid": SIDEBAR_TEST_IDS.ADMINISTRATION,
  permissions: [
    {
      do: ACTION_LIST,
      on: SUBJECT_WORKFLOWS,
    },
  ],
  permissionOneOf: true,
  children: [
    {
      id: SIDEBAR_IDS.administrationWorkflows,
      title: COMMON_TRANSLATION_KEYS.Workflows,
      url: workflowsRoutes.path,
      isActive,
      "data-testid": SIDEBAR_TEST_IDS.WORKFLOWS,
      permissions: [
        {
          do: ACTION_LIST,
          on: SUBJECT_WORKFLOWS,
        },
      ],
    },
  ],
};

function getVoltAccountsChildren(): SidebarConfigItem[] {
  return [
    {
      id: SIDEBAR_IDS.voltAccountsAccounts,
      title: COMMON_TRANSLATION_KEYS.accountsSidebarTitle,
      url: VOLT_ACCOUNTS_PAGE_URLS.accounts,
      isActive: (_, activePath: string) =>
        activePath.startsWith(VOLT_ACCOUNTS_PAGE_URLS.voltAccounts) ||
        activePath.startsWith(VOLT_ACCOUNTS_PAGE_URLS.namedAccounts),
      "data-testid": SIDEBAR_TEST_IDS.VOLT_ACCOUNTS_ACCOUNTS,
      permissions: [
        {
          do: ACTION_DETAILS,
          on: SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS,
        },
      ],
    },
    {
      id: SIDEBAR_IDS.voltAccountsPayments,
      title: COMMON_TRANSLATION_KEYS.payments,
      url: ACCOUNTS_PAYMENTS_PAGE_URLS.payments,
      isActive,
      "data-testid": SIDEBAR_TEST_IDS.VOLT_ACCOUNTS_PAYMENTS,
      permissions: [
        {
          do: ACTION_DETAILS,
          on: SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS,
        },
      ],
    },
    {
      id: SIDEBAR_IDS.voltAccountsConfiguration,
      title: COMMON_TRANSLATION_KEYS.configuration,
      url: VOLT_ACCOUNTS_PAGE_URLS.configuration,
      isActive,
      "data-testid": SIDEBAR_TEST_IDS.VOLT_ACCOUNTS_CONFIGURATION,
      permissionOneOf: true,
      permissions: [
        {
          do: ACTION_LIST,
          on: SUBJECT_CREDIT_NOTIFICATION_PREFERENCES,
        },
        {
          do: ACTION_DETAILS,
          on: SUBJECT_PAYOUTS_NOTIFICATIONS,
        },
        {
          do: ACTION_LIST,
          on: SUBJECT_CASH_MANAGEMENT_SETTLEMENT_NOTIFICATION,
        },
      ],
    },
    {
      id: SIDEBAR_IDS.voltAccountsBeneficiaries,
      title: COMMON_TRANSLATION_KEYS.beneficiaries,
      url: VOLT_ACCOUNTS_PAGE_URLS.beneficiaries,
      isActive,
      "data-testid": SIDEBAR_TEST_IDS.VOLT_ACCOUNTS_BENEFICIARIES,
      permissions: [
        {
          do: ACTION_LIST,
          on: SUBJECT_CASH_MANAGEMENT_VOLT_ACCOUNTS,
        },
      ],
    },
  ];
}
